<template>
  <div
    v-if="value"
    class="pick-responsibility-dialog-overlay"
    @click.self="handleOverlayClick"
  >
    <div class="pick-responsibility-dialog">
      <!-- Modal Header -->
      <div class="modal-header">
        <div class="header-content">
          <div class="featured-icon">
            <img :src="require('@components/assets/icons/badge.svg')" alt="badge icon" />
          </div>
        </div>
        <button
          class="close-button"
          @click="handleClose"
          aria-label="Close dialog"
        >
          <Icon name="interface_close" :size="24" />
        </button>
      </div>

      <div class="text-content">
        <h2 class="dialog-title-modal">Issue a badge to an Employee</h2>
      </div>

      <!-- Content -->
      <div class="dialog-content">
        <ActiveResponsibilityPicker
          :org_object_type="'Responsibility'"
          :items="items"
          :isSearch="true"
          :hasMoreData="hasMoreData"
          :isLoadingMore="isLoadingMore"
          :customPaginationForStorybook="customPaginationForStorybook"
          :allowed_statuses="allowed_statuses"
          :hide_disabled="hide_disabled"
          @item-selected="handleResponsibilitySelected"
          @clear-selection="handleResponsibilityCleared"
          @search="handleSearch"
          @load-more="handleLoadMore"
        />
      </div>

      <!-- Modal Actions -->
      <div class="modal-actions">
        <div class="actions-content">
          <div class="actions-right">
            <StandardButton
              type="magenta"
              variant="primary"
              size="normal"
              label="Next"
              rightIcon="arrowRight"
              :disabled="!isValid"
              @click="handleNext"
              class="next-button-full-width"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from "vue";
import Icon from "@components/icons/Icon.vue";
import ActiveResponsibilityPicker from "@components/ActiveResponsibilityPicker/ActiveResponsibilityPicker.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";

const props = defineProps({
  value: {
    type: Boolean,
    default: false,
  },
  items: {
    type: Array,
    default: () => [],
  },
  hasMoreData: {
    type: Boolean,
    default: false,
  },
  isLoadingMore: {
    type: Boolean,
    default: false,
  },
  customPaginationForStorybook: {
    type: Boolean,
    default: false,
  },
  allowed_statuses: {
    type: Array,
    default: () => ["active"],
  },
  hide_disabled: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(["input", "next", "close", "search", "load-more"]);

const selectedResponsibility = ref(null);

const isValid = computed(() => {
  return selectedResponsibility.value !== null;
});

watch(
  () => props.value,
  (newVal) => {
    if (newVal) {
      // Reset selection when dialog opens
      selectedResponsibility.value = null;
    }
  }
);

const handleResponsibilitySelected = (item) => {
  selectedResponsibility.value = item;
};

const handleResponsibilityCleared = () => {
  selectedResponsibility.value = null;
};

const handleSearch = (searchTerm) => {
  emit("search", searchTerm);
};

const handleLoadMore = () => {
  emit("load-more");
};

const handleOverlayClick = () => {
  handleClose();
};

const handleClose = () => {
  emit("input", false);
  emit("close");
};

const handleNext = () => {
  if (isValid.value && selectedResponsibility.value) {
    emit("next", {
      responsibility: selectedResponsibility.value,
    });
  }
};

// Handle ESC key
const handleKeydown = (event) => {
  if (event.key === "Escape" && props.value) {
    handleClose();
  }
};

onMounted(() => {
  document.addEventListener("keydown", handleKeydown);
});

onUnmounted(() => {
  document.removeEventListener("keydown", handleKeydown);
});
</script>

<style scoped>
.pick-responsibility-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.pick-responsibility-dialog {
  background: white;
  background-image: url("@components/assets/Background_pattern_decorative_new.svg");
  background-repeat: no-repeat;
  background-position: top left;
  border-radius: 10px;
  width: 100%;
  max-width: 723px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0px 8px 8px -4px rgba(16, 24, 40, 0.08),
    0px 20px 24px -4px rgba(16, 24, 40, 0.14);
  position: relative;
}

.modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  position: relative;
  padding: 24px 24px 0 24px;
  width: 100%;
  z-index: 1;
}

.header-content {
  display: flex;
  gap: 12px;
  flex: 1;
}

.featured-icon {
  background: white;
  border: 1px solid #eaecf0;
  border-radius: 10px;
  box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05);
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.text-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  color: #101828;
  text-align: left;
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
  margin-top: 16px;
  margin-bottom: 16px;
}

.dialog-title-modal {
  font-weight: 600;
  font-size: 18px;
  line-height: 28px;
  color: #101828;
  text-align: center;
  margin: 0;
}

.close-button {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border-radius: 8px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #98a2b3;
  transition: background-color 0.2s;
}

.close-button:hover {
  background-color: rgba(152, 162, 179, 0.1);
}

.dialog-content {
  padding-inline: 24px;
  width: 100%;
}

.modal-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  padding-top: 0;
  width: 100%;
  margin-top: 30px;
  padding-inline: 24px;
}

.actions-content {
  display: flex;
  gap: 12px;
  align-items: center;
  padding-bottom: 24px !important;
  width: 100%;
}

.actions-right {
  display: flex;
  flex: 1 0 0;
  gap: 12px;
  align-items: center;
  justify-content: stretch;
  min-width: 0;
  width: 100%;
}

.next-button-full-width {
  width: 100% !important;
  flex: 1 0 0;
  cursor: pointer !important;
}

::v-deep .next-button-full-width button {
  width: 100% !important;
}
</style>
